import { apiClient } from './client'
import { ApiError } from './errors'
import type { UserLookupResult } from '@/types/domain'

// The record itself is declared once, in the domain types; it is re-exported
// here so a caller of lookupByEmail does not need a second import.
export type { UserLookupResult }

/**
 * The keys `PATCH /v1/users/me` honours, in the camelCase the rest of the API
 * speaks.
 *
 * The gateway binds both spellings and lets camelCase win a body carrying
 * both, so this sends camelCase only — one spelling, and the contract's. (A
 * camelCase body used to answer 200 having changed nothing, which is the
 * worst failure available: the caller cannot tell it was ignored. Any
 * snake_case workaround in a screen can go.)
 *
 * An absent key leaves the column alone; an explicit `''` clears it. Typed
 * rather than a free `Record` because a key outside this set is accepted and
 * dropped — a typo would look like a save that worked.
 */
export interface ProfilePatch {
  firstName?: string
  lastName?: string
  /**
   * Only when the person typed a display name of their own. Leaving it out
   * while sending `firstName`/`lastName` is not a gap: the gateway then
   * rebuilds `display_name` as "Vorname Nachname" from the resulting values,
   * reading the half that was not sent off the stored row — so the rail
   * avatar, the account menu and every sharing list follow a rename instead
   * of keeping the stale name. Sending it explicitly still wins.
   *
   * Clearing both name parts does **not** blank the display name: an account
   * whose identity came from Google or Microsoft has only that name.
   */
  displayName?: string
  profilePicture?: string
}

export const usersApi = {
  async patchMe(updates: ProfilePatch): Promise<void> {
    await apiClient.axios.patch('/v1/users/me', updates)
  },

  /** Best-effort: tracking recent documents must never break the page that
   *  triggers it (e.g. a transient 401 mid-token-refresh). */
  async addRecentDocument(documentId: string): Promise<void> {
    try {
      await apiClient.axios.post('/v1/users/me/recent-documents', { document_id: documentId })
    } catch {
      /* ignored on purpose */
    }
  },

  /** Resolves an email to a uid for the template-access invite flow.
   *  Returns null on 404 (`user_not_found`) — an expected outcome there. */
  async lookupByEmail(email: string): Promise<UserLookupResult | null> {
    try {
      const { data } = await apiClient.axios.get<UserLookupResult>('/v1/users/lookup', {
        params: { email },
      })
      return data
    } catch (error) {
      if (ApiError.from(error).isNotFound) return null
      throw error
    }
  },
}
